import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { getMyTenant, TENANT_ME_QUERY_KEY } from '@/lib/api/tenants';
import {
  revokeCarrierConnection,
  type CarrierConnectionSummary,
  type CarrierKind,
} from '@/lib/api/carrier-connections';

import { CarrierCard } from './CarrierCard';
import { ORDERED_CARRIER_KINDS } from './carrier-catalogue';
import { OnboardingShell } from './OnboardingShell';
import { RoyalMailCdConnectDialog } from './RoyalMailCdConnectDialog';
import { StepIndicator } from './StepIndicator';

/**
 * Step 4 of 5 — connect a carrier.
 *
 * Card grid lists every carrier Mailchute will support; live ones
 * (only `royalmail_cd` at MVP) show Connect/Manage CTAs, the rest
 * are "Coming soon" tiles. Per ADR 0015 + §10.1.3 the step stays
 * skippable — a tenant can finish onboarding with zero carriers
 * connected and add one later from settings.
 */
export function Step4Carrier() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const tenantQuery = useQuery({
    queryKey: TENANT_ME_QUERY_KEY,
    queryFn: async () => {
      const result = await getMyTenant();
      if (result.isErr()) throw new Error(result.error.message);
      return result.value;
    },
  });
  const carrierConnections = tenantQuery.data?.carrier_connections ?? [];
  const activeByKind = new Map<CarrierKind, CarrierConnectionSummary>(
    carrierConnections
      .filter((c) => c.status === 'active')
      .map((c) => [c.kind, c] satisfies [CarrierKind, CarrierConnectionSummary]),
  );

  const [royalMailCdDialogOpen, setRoyalMailCdDialogOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState<string | null>(null);

  async function disconnect(carrierConnectionId: string) {
    setDisconnecting(carrierConnectionId);
    const result = await revokeCarrierConnection(carrierConnectionId);
    setDisconnecting(null);
    if (result.isErr()) {
      const message =
        result.error.kind === 'unauthorised'
          ? 'Your session expired. Please sign in again.'
          : "We couldn't disconnect that carrier. Please try again.";
      toast.error(message);
      return;
    }
    await qc.invalidateQueries({ queryKey: TENANT_ME_QUERY_KEY });
  }

  async function onConnected() {
    setRoyalMailCdDialogOpen(false);
    await qc.invalidateQueries({ queryKey: TENANT_ME_QUERY_KEY });
    toast.success('Carrier connected.');
  }

  const connectedCount = activeByKind.size;
  const totalCount = ORDERED_CARRIER_KINDS.length;

  return (
    <OnboardingShell wide>
      <StepIndicator currentStep={4} />
      <Card>
        <CardHeader>
          <CardTitle>Connect a carrier</CardTitle>
          <CardDescription>
            Pick the carriers you ship with. You can always add more — or skip and configure later
            from settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-muted-foreground text-xs">
            {connectedCount} of {totalCount} connected · Click & Drop available now, the rest land
            soon.
          </p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="carrier-grid">
            {ORDERED_CARRIER_KINDS.map((kind) => (
              <CarrierCard
                key={kind}
                kind={kind}
                activeConnection={activeByKind.get(kind)}
                disconnecting={
                  disconnecting !== null &&
                  disconnecting === activeByKind.get(kind)?.carrier_connection_id
                }
                onConnect={() => {
                  if (kind === 'royalmail_cd') {
                    setRoyalMailCdDialogOpen(true);
                  }
                }}
                onDisconnect={(id) => {
                  void disconnect(id);
                }}
              />
            ))}
          </div>
          <div className="flex justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                void navigate('/app/onboarding/step/3');
              }}
            >
              Back
            </Button>
            <Button
              type="button"
              onClick={() => {
                void navigate('/app/onboarding/step/5');
              }}
            >
              {connectedCount > 0 ? 'Continue' : 'Skip for now'}
            </Button>
          </div>
        </CardContent>
      </Card>
      <RoyalMailCdConnectDialog
        open={royalMailCdDialogOpen}
        onClose={() => {
          setRoyalMailCdDialogOpen(false);
        }}
        onConnected={() => {
          void onConnected();
        }}
      />
    </OnboardingShell>
  );
}
